/** Primitives shared by every screen. Thin wrappers over the CSS in app.css. */

import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactElement, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cloneElement, isValidElement, useEffect, useId, useRef } from 'react';
import { BAND_LABEL, BAND_ORDER, type MasteryBand, type Spread } from '../lib/types';

/* ---------------------------------------------------------------- buttons */

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  block?: boolean;
};

export function Button({ variant = 'secondary', size = 'md', block, className = '', ...rest }: ButtonProps) {
  const classes = [
    'btn',
    `btn--${variant}`,
    size !== 'md' ? `btn--${size}` : '',
    block ? 'btn--block' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return <button type="button" className={classes} {...rest} />;
}

/* ----------------------------------------------------------------- fields */

/**
 * Wraps a control with its label, hint and error.
 *
 * The control is associated by id rather than by nesting: a nested `<label>` folds the
 * hint and error text into the accessible name, so a screen reader announces
 * "Your name So your teacher knows which answers are yours" as the field's name. Pointing
 * `aria-describedby` at the hint keeps it as description, where it belongs.
 */
export function Field({
  label,
  hint,
  error,
  children,
}: {
  label?: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  const control = isValidElement(children)
    ? cloneElement(children as ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? id,
        ...(message ? { 'aria-describedby': messageId } : {}),
        ...(error ? { 'aria-invalid': true } : {}),
      })
    : children;

  return (
    <div className="field">
      {label && (
        <label className="field__label" htmlFor={id}>
          {label}
        </label>
      )}
      {control}
      {message && (
        <span className={error ? 'field__error' : 'field__hint'} id={messageId} {...(error ? { role: 'alert' } : {})}>
          {message}
        </span>
      )}
    </div>
  );
}

export function Input(props: InputHTMLAttributes<HTMLInputElement>) {
  return <input className="input" {...props} />;
}

export function Textarea(props: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className="textarea" {...props} />;
}

export function Select({ children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className="select" {...rest}>
      {children}
    </select>
  );
}

/* ------------------------------------------------------------------ cards */

export function Card({
  title,
  note,
  actions,
  children,
  flush,
  quiet,
  className = '',
}: {
  title?: ReactNode;
  note?: ReactNode;
  actions?: ReactNode;
  children?: ReactNode;
  flush?: boolean;
  quiet?: boolean;
  className?: string;
}) {
  return (
    <section className={`card ${flush ? 'card--flush' : ''} ${quiet ? 'card--quiet' : ''} ${className}`}>
      {(title || actions) && (
        <header className="card__head" style={flush ? { padding: '18px 20px 0' } : undefined}>
          <div>
            {title && <h2 className="card__title">{title}</h2>}
            {note && <div className="card__note">{note}</div>}
          </div>
          {actions && <div className="row row--tight">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/* ------------------------------------------------------------------ pills */

export function Pill({
  tone = 'default',
  small,
  children,
}: {
  tone?: 'default' | 'solid' | 'brand' | 'good' | 'warn' | 'bad';
  small?: boolean;
  children: ReactNode;
}) {
  return (
    <span className={`pill ${tone !== 'default' ? `pill--${tone}` : ''} ${small ? 'pill--sm' : ''}`}>{children}</span>
  );
}

/**
 * A mastery band, always with its label. Colour is a second signal, never the only one —
 * the accessibility rules on screen 10i make that non-negotiable.
 */
export function Band({ band, label }: { band: MasteryBand; label?: string }) {
  return <span className={`band band--${band}`}>{label ?? BAND_LABEL[band]}</span>;
}

/** One cell of the student × topic grid (5d). The title is what makes it readable. */
export function BandCell({ band, title }: { band: MasteryBand; title: string }) {
  return <span className={`band-cell band-cell--${band}`} title={`${title} — ${BAND_LABEL[band]}`} aria-label={`${title}: ${BAND_LABEL[band]}`} />;
}

/** Distribution of a cohort across the bands, with a text equivalent for screen readers. */
export function SpreadBar({ spread, showCounts }: { spread: Spread; showCounts?: boolean }) {
  const total = BAND_ORDER.reduce((a, b) => a + (spread[b] ?? 0), 0) + (spread.no_evidence ?? 0);
  if (total === 0) return <span className="micro muted">No evidence yet</span>;
  const parts = [...BAND_ORDER, 'no_evidence' as const];
  return (
    <div className="stack" style={{ gap: 5 }}>
      <div className="spread" role="img" aria-label={parts.map((b) => `${spread[b] ?? 0} ${BAND_LABEL[b]}`).join(', ')}>
        {parts.map((b) =>
          (spread[b] ?? 0) > 0 ? (
            <span key={b} className={`spread__seg spread__seg--${b}`} style={{ width: `${((spread[b] ?? 0) / total) * 100}%` }} />
          ) : null,
        )}
      </div>
      {showCounts && (
        <div className="row row--tight micro muted">
          {parts.map((b) => ((spread[b] ?? 0) > 0 ? <span key={b}>{spread[b]} {BAND_LABEL[b].toLowerCase()}</span> : null))}
        </div>
      )}
    </div>
  );
}

export function Avatar({ initials, size = 'md' }: { initials: string; size?: 'sm' | 'md' | 'lg' }) {
  return <span className={`avatar ${size !== 'md' ? `avatar--${size}` : ''}`} aria-hidden="true">{initials}</span>;
}

/* ------------------------------------------------------------------ state */

export function Empty({ title, body, action }: { title: string; body?: string; action?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {body && <p className="empty__body">{body}</p>}
      {action}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="empty" aria-live="polite">
      <div className="empty__body">{label}…</div>
    </div>
  );
}

export function ErrorNote({ error, retry }: { error: unknown; retry?: () => void }) {
  const message = error instanceof Error ? error.message : 'Something went wrong';
  return (
    <div className="banner banner--bad" role="alert">
      <span>{message}</span>
      {retry && (
        <Button size="sm" variant="ghost" onClick={retry}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function Banner({ tone = 'info', children }: { tone?: 'info' | 'good' | 'warn' | 'bad'; children: ReactNode }) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'bad' ? 'alert' : undefined}>
      {children}
    </div>
  );
}

export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <div className="stat">
      <span className="stat__value">{value}</span>
      <span className="stat__label">{label}</span>
    </div>
  );
}

export function Progress({ value, max, label }: { value: number; max: number; label?: string }) {
  const fraction = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  return (
    <div className="stack" style={{ gap: 5 }}>
      {label && <div className="row row--between micro muted"><span>{label}</span><span>{value} of {max}</span></div>}
      <div className="progress" role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={max}>
        <div className="progress__bar" style={{ transform: `scaleX(${fraction})` }} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------- tabs */

export function Tabs<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: string }[];
  label: string;
}) {
  return (
    <div className="tabs" role="tablist" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="tab"
          className="tab"
          aria-selected={value === o.value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/**
 * The one toggle every performance screen carries (5a): content lens or skill lens,
 * and nothing else.
 */
export function LensToggle({ lens, onChange }: { lens: 'content' | 'skill'; onChange: (l: 'content' | 'skill') => void }) {
  return (
    <Tabs
      label="Performance lens"
      value={lens}
      onChange={onChange}
      options={[
        { value: 'content', label: 'Content' },
        { value: 'skill', label: 'Skill' },
      ]}
    />
  );
}

/* ----------------------------------------------------------------- dialog */

export function Dialog({
  title,
  children,
  actions,
  onClose,
  wide,
}: {
  title: string;
  children: ReactNode;
  actions?: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    // Move focus into the dialog so a keyboard user is not left behind it.
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="scrim" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog ${wide ? 'dialog--wide' : ''}`} role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={ref}>
        <h2 className="dialog__title" id={titleId}>
          {title}
        </h2>
        <div className="dialog__body">{children}</div>
        {actions && <div className="dialog__actions">{actions}</div>}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ misc */

export function Steps({ current, labels }: { current: number; labels: string[] }) {
  return (
    <ol className="steps" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {labels.map((l, i) => (
        <li key={l} className={`step ${i === current ? 'step--active' : ''} ${i < current ? 'step--done' : ''}`}>
          <span className="step__num">{i < current ? '✓' : i + 1}</span>
          {l}
        </li>
      ))}
    </ol>
  );
}

export function Crumbs({ items }: { items: { label: string; to?: string }[] }) {
  return (
    <nav className="crumbs" aria-label="Breadcrumb">
      {items.map((item, i) => (
        <span key={`${item.label}-${i}`} className="row row--tight">
          {item.to ? <a href={item.to}>{item.label}</a> : <span>{item.label}</span>}
          {i < items.length - 1 && <span aria-hidden="true">›</span>}
        </span>
      ))}
    </nav>
  );
}

/** Wide content scrolls in its own container so the page never scrolls sideways (10i). */
export function ScrollX({ children }: { children: ReactNode }) {
  return <div className="scroll-x">{children}</div>;
}

export function formatMinutes(total: number): string {
  if (total < 60) return `${total} min`;
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m === 0 ? `${h} hr` : `${h} hr ${m} min`;
}

export function formatDate(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function relativeDay(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso);
  const days = Math.round((then.getTime() - Date.now()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 1 && days < 7) return then.toLocaleDateString(undefined, { weekday: 'long' });
  return formatDate(iso);
}
